import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { dirFor } from '@/i18n';

import { ApiError, auth, demo } from '@/api/client';
import { Logo } from '@/components/brand/logo';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

export const Route = createFileRoute('/login')({
  component: LoginPage,
});

type Step = { kind: 'password' } | { kind: 'mfa'; challenge: string };

function LoginPage() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [step, setStep] = useState<Step>({ kind: 'password' });
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');

  // Login is reachable before AppShell mounts, so wire dir/lang here too.
  useEffect(() => {
    document.documentElement.lang = i18n.language;
    document.documentElement.dir = dirFor(i18n.language);
  }, [i18n.language]);

  const demoQuery = useQuery({
    queryKey: ['demo-info'],
    queryFn: demo.info,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  const passwordMutation = useMutation({
    mutationFn: async () => auth.login(username, password),
    onSuccess: (resp) => {
      if (resp.requires_mfa) {
        setStep({ kind: 'mfa', challenge: resp.challenge_token });
        toast.message(t('auth.mfa_title'), { description: t('auth.mfa_help') });
        return;
      }
      navigate({ to: '/dashboard' });
    },
    onError: (e) => toast.error(extractMessage(e, t('auth.invalid_credentials'))),
  });

  const mfaMutation = useMutation({
    mutationFn: async () => {
      if (step.kind !== 'mfa') throw new Error('not in MFA step');
      return auth.loginMfa(step.challenge, code);
    },
    onSuccess: () => navigate({ to: '/dashboard' }),
    onError: (e) => toast.error(extractMessage(e, t('auth.invalid_code'))),
  });

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (step.kind === 'password') passwordMutation.mutate();
    else mfaMutation.mutate();
  };

  return (
    <main className="auth-backdrop min-h-screen grid place-items-center p-4">
      <Card className="w-full max-w-sm shadow-elev">
        <CardHeader className="items-center text-center space-y-3 pb-4">
          <Logo size="xl" className="drop-shadow-sm" />
          <div className="space-y-1">
            <CardTitle className="text-xl">{t('auth.sign_in_title')}</CardTitle>
            <CardDescription>
              {step.kind === 'password' ? t('auth.sign_in_subtitle') : t('auth.mfa_help')}
            </CardDescription>
          </div>
        </CardHeader>
        <CardContent>
          {step.kind === 'password' && demoQuery.data ? (
            <div
              className="mb-4 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-600 dark:bg-amber-950/40 dark:text-amber-200"
              data-testid="demo-banner"
            >
              {demoQuery.data.mode === 'request_access' ? (
                <>
                  <div className="font-medium">{t('auth.demo_request_title')}</div>
                  <p className="mt-1 text-xs leading-snug opacity-80">
                    {t('auth.demo_request_subtitle')}
                  </p>
                  <div className="mt-3 flex flex-wrap gap-2">
                    <Button asChild size="sm">
                      <Link to="/demo/request">{t('auth.demo_request_cta')}</Link>
                    </Button>
                    <Button asChild size="sm" variant="outline">
                      <Link to="/demo/login">{t('auth.demo_have_token_cta')}</Link>
                    </Button>
                  </div>
                  <p className="mt-2 text-xs leading-snug opacity-80">{demoQuery.data.notice}</p>
                </>
              ) : (
                (() => {
                  const legacy = demoQuery.data;
                  return (
                    <>
                      <div className="flex items-center justify-between gap-2">
                        <span className="font-medium">{t('auth.demo_banner_title')}</span>
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setUsername(legacy.username);
                            setPassword(legacy.password);
                          }}
                        >
                          {t('auth.demo_use_credentials')}
                        </Button>
                      </div>
                      <dl className="mt-2 grid grid-cols-[max-content_1fr] gap-x-3 gap-y-0.5 font-mono text-xs">
                        <dt className="opacity-70">{t('auth.username')}</dt>
                        <dd className="select-all">{legacy.username}</dd>
                        <dt className="opacity-70">{t('auth.password')}</dt>
                        <dd className="select-all">{legacy.password}</dd>
                      </dl>
                      <p className="mt-2 text-xs leading-snug opacity-80">{legacy.notice}</p>
                    </>
                  );
                })()
              )}
            </div>
          ) : null}
          <form className="space-y-4" onSubmit={onSubmit}>
            {step.kind === 'password' ? (
              <>
                <div className="space-y-1.5">
                  <Label htmlFor="username">{t('auth.username')}</Label>
                  <Input
                    id="username"
                    autoComplete="username"
                    autoFocus
                    required
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="password">{t('auth.password')}</Label>
                  <Input
                    id="password"
                    type="password"
                    autoComplete="current-password"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                </div>
              </>
            ) : (
              <div className="space-y-1.5">
                <Label htmlFor="otp">{t('auth.mfa_code')}</Label>
                <Input
                  id="otp"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={8}
                  autoFocus
                  required
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/[^\d]/g, ''))}
                />
              </div>
            )}

            <Button
              type="submit"
              className="w-full"
              disabled={passwordMutation.isPending || mfaMutation.isPending}
            >
              {step.kind === 'password' ? t('auth.sign_in') : t('auth.mfa_verify')}
            </Button>

            {step.kind === 'mfa' && (
              <Button
                type="button"
                variant="ghost"
                className="w-full"
                onClick={() => {
                  setStep({ kind: 'password' });
                  setCode('');
                }}
              >
                {t('common.back')}
              </Button>
            )}
          </form>
        </CardContent>
      </Card>
    </main>
  );
}

function extractMessage(e: unknown, fallback: string): string {
  if (e instanceof ApiError) return e.message || fallback;
  if (e instanceof Error) return e.message || fallback;
  return fallback;
}
